import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';

const BOOKING_URL = 'https://cryobybreum.planway.com/';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [treatmentsDropdownOpen, setTreatmentsDropdownOpen] = useState(false);
  const [mobileTreatmentsOpen, setMobileTreatmentsOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setTreatmentsDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setTreatmentsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isTreatmentActive = location.pathname.startsWith('/behandlinger') || 
    ['/fedtfrysning', '/ems-traening', '/body-roll'].includes(location.pathname);

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 bg-white border-b border-taupe-border/50 shadow-xs ${
        isScrolled ? 'py-3.5' : 'py-4 sm:py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between min-[1200px]:grid min-[1200px]:grid-cols-[1fr_auto_1fr] min-[1200px]:gap-8">
          {/* Desktop Left Navigation - Strictly >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center justify-start gap-8" aria-label="Venstre navigation">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `text-sm tracking-wide font-medium transition-colors py-1 relative ${
                  isActive && location.pathname === '/'
                    ? 'text-espresso font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-accent-brown'
                    : 'text-espresso-light/80 hover:text-espresso'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Behandlinger Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setTreatmentsDropdownOpen(!treatmentsDropdownOpen)}
                onMouseEnter={() => setTreatmentsDropdownOpen(true)}
                className={`flex items-center gap-1.5 text-sm tracking-wide font-medium transition-colors py-1 cursor-pointer relative ${
                  isTreatmentActive
                    ? 'text-espresso font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-accent-brown'
                    : 'text-espresso-light/80 hover:text-espresso'
                }`}
                aria-expanded={treatmentsDropdownOpen}
              >
                <span>Behandlinger</span>
                <i className={`fa-solid fa-chevron-down text-[10px] text-taupe transition-transform duration-200 ${treatmentsDropdownOpen ? 'rotate-180 text-espresso' : ''}`} aria-hidden="true" />
              </button>

              {/* Dropdown Menu */}
              {treatmentsDropdownOpen && (
                <div 
                  onMouseLeave={() => setTreatmentsDropdownOpen(false)}
                  className="absolute left-0 top-full mt-2 w-52 bg-white shadow-lg border border-taupe-border/80 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                >
                  <NavLink
                    to="/behandlinger/fedtfrysning"
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        isActive ? 'text-espresso font-bold' : 'text-espresso/80 hover:text-espresso'
                      }`
                    }
                  >
                    Fedtfrysning
                  </NavLink>
                  <NavLink
                    to="/behandlinger/ems-traening"
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        isActive ? 'text-espresso font-bold' : 'text-espresso/80 hover:text-espresso'
                      }`
                    }
                  >
                    EMS Træning
                  </NavLink>
                  <NavLink
                    to="/behandlinger/body-roll"
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        isActive ? 'text-espresso font-bold' : 'text-espresso/80 hover:text-espresso'
                      }`
                    }
                  >
                    Body Roll
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink
              to="/priser"
              className={({ isActive }) =>
                `text-sm tracking-wide font-medium transition-colors py-1 relative ${
                  isActive
                    ? 'text-espresso font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-accent-brown'
                    : 'text-espresso-light/80 hover:text-espresso'
                }`
              }
            >
              Priser
            </NavLink>
          </nav>

          {/* Centered Logo */}
          <div className="flex justify-start min-[1200px]:justify-center">
            <Link 
              to="/" 
              className="flex items-center group focus:outline-hidden"
              aria-label="Cryo by Breum - Forside"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=360,quality=90,format=auto/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-logo.png"
                alt="CRYO BY BREUM logo - Klinik for kropsbehandlinger og træning i Odense"
                className="h-6 sm:h-7.5 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Right Navigation & Actions + Mobile Burger Trigger */}
          <div className="flex items-center justify-end gap-6 sm:gap-8">
            <nav className="hidden min-[1200px]:flex items-center gap-8" aria-label="Højre navigation">
              <NavLink
                to="/om-cryo-by-breum"
                className={({ isActive }) =>
                  `text-sm tracking-wide font-medium transition-colors py-1 relative ${
                    isActive
                      ? 'text-espresso font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-accent-brown'
                      : 'text-espresso-light/80 hover:text-espresso'
                  }`
                }
              >
                Om Cryo by Breum
              </NavLink>

              <NavLink
                to="/kontakt"
                className={({ isActive }) =>
                  `text-sm tracking-wide font-medium transition-colors py-1 relative ${
                    isActive
                      ? 'text-espresso font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-accent-brown'
                      : 'text-espresso-light/80 hover:text-espresso'
                  }`
                }
              >
                Kontakt
              </NavLink>

              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-5 py-2.5 bg-accent-brown text-white text-xs font-semibold uppercase tracking-widest hover:bg-accent-brown-hover transition-all shadow-xs cursor-pointer focus:outline-hidden"
              >
                <span>Book tid</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px] ml-2 opacity-80" aria-hidden="true" />
              </a>
            </nav>

            {/* Burger menu trigger - strictly visible under 1200px */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="min-[1200px]:hidden inline-flex items-center justify-center p-2 text-espresso hover:text-accent-brown transition-colors cursor-pointer focus:outline-hidden bg-transparent"
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-xl' : 'fa-bars text-lg'}`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile & Tablet Drawer Menu (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-taupe-border/80 px-4 pt-3 pb-6 shadow-xl animate-in slide-in-from-top-4 duration-200">
          <div className="max-w-md mx-auto space-y-2">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `block px-4 py-3 text-base font-medium transition-colors ${
                  isActive && location.pathname === '/' ? 'text-espresso font-semibold' : 'text-espresso-light hover:text-espresso'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Mobile Treatments Accordion */}
            <div className="overflow-hidden">
              <button
                type="button"
                onClick={() => setMobileTreatmentsOpen(!mobileTreatmentsOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-espresso text-left cursor-pointer hover:text-accent-brown transition-colors"
              >
                <span>Behandlinger</span>
                <i className={`fa-solid fa-chevron-down text-xs text-taupe transition-transform duration-200 ${mobileTreatmentsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>

              {mobileTreatmentsOpen && (
                <div className="pl-6 pr-2 py-1 space-y-1">
                  <NavLink
                    to="/behandlinger/fedtfrysning"
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm font-medium transition-colors ${
                        isActive ? 'text-espresso font-semibold' : 'text-espresso/75 hover:text-espresso'
                      }`
                    }
                  >
                    Fedtfrysning
                  </NavLink>
                  <NavLink
                    to="/behandlinger/ems-traening"
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm font-medium transition-colors ${
                        isActive ? 'text-espresso font-semibold' : 'text-espresso/75 hover:text-espresso'
                      }`
                    }
                  >
                    EMS Træning
                  </NavLink>
                  <NavLink
                    to="/behandlinger/body-roll"
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm font-medium transition-colors ${
                        isActive ? 'text-espresso font-semibold' : 'text-espresso/75 hover:text-espresso'
                      }`
                    }
                  >
                    Body Roll
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink
              to="/priser"
              className={({ isActive }) =>
                `block px-4 py-3 text-base font-medium transition-colors ${
                  isActive ? 'text-espresso font-semibold' : 'text-espresso-light hover:text-espresso'
                }`
              }
            >
              Priser
            </NavLink>

            <NavLink
              to="/om-cryo-by-breum"
              className={({ isActive }) =>
                `block px-4 py-3 text-base font-medium transition-colors ${
                  isActive ? 'text-espresso font-semibold' : 'text-espresso-light hover:text-espresso'
                }`
              }
            >
              Om Cryo by Breum
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `block px-4 py-3 text-base font-medium transition-colors ${
                  isActive ? 'text-espresso font-semibold' : 'text-espresso-light hover:text-espresso'
                }`
              }
            >
              Kontakt
            </NavLink>

            <div className="pt-3">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3 bg-accent-brown text-white text-xs font-semibold uppercase tracking-widest text-center shadow-xs"
              >
                <span>Book tid på Planway</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
